import { plainToInstance, Type } from 'class-transformer'
import { Entity } from '@core/entities/entity'
import type {
  ScheduledInspectionLevel,
  ScheduledInspectionPermittedAction,
  ScheduledInspectionUpdatePayloadType,
  ScheduleInspectionFormValuesType,
  ScheduleInspectionFrequencyType,
} from './types'
import { localizedStrings } from '@core/strings'
import { User } from '@core/entities/user'
import { Checklist } from '@core/entities/checklist/Checklist'
import { RegisterItem } from '@core/entities/register/RegisterItem'
import type { IScheduledInspection } from './IScheduledInspection'
import { RegisterScheduleInspectionsSchedulingConfig } from './RegisterScheduleInspectionsSchedulingConfig'
import moment from 'moment'

export class ScheduledInspection extends Entity<IScheduledInspection> {
  @Type(() => User)
  assignedTo: User

  canSubmitInspection: boolean

  checklistTemplates: Array<{
    id: number
    name: string
  }>

  completedRuns: number

  id: number

  // null on project-level rows
  registerTemplateInspectionId: string | null

  nextInspectionDue: string

  // todo: make this mandatory prop once the backend is updated
  openTasks?: number

  @Type(() => RegisterScheduleInspectionsSchedulingConfig)
  scheduleConfig: RegisterScheduleInspectionsSchedulingConfig

  @Type(() => User)
  scheduleCreatedBy: User

  // The fields below are only sent by the project scheduled inspections endpoint
  level?: ScheduledInspectionLevel

  inspectionTemplate?: {
    templateId: number
    name: string
  }

  overdue?: boolean

  // null on project-level rows
  @Type(() => RegisterItem)
  registerItem?: RegisterItem | null

  lastInspectionTime?: string

  @Type(() => User)
  lastInspectedBy?: User

  @Type(() => Checklist)
  lastSubmittedChecklist?: Checklist

  permittedActions?: Array<ScheduledInspectionPermittedAction>

  public static frequencyOptionLabel(
    value: ScheduleInspectionFrequencyType,
  ): string {
    switch (value) {
      case 'ONCE':
        return localizedStrings.once
      case 'DAILY':
        return localizedStrings.daily
      case 'WEEKLY':
        return localizedStrings.weekly
      case 'MONTHLY':
        return localizedStrings.monthly
      case 'CUSTOM':
        return localizedStrings.custom
      default:
        return ''
    }
  }

  public getScheduleDisplayLabel(): string {
    if (this.scheduleConfig.type === 'ONCE') {
      return localizedStrings.once
    }
    return localizedStrings.everyNDays(this.scheduleConfig.frequencyInDays)
  }

  public isCompleted(): boolean {
    return this.scheduleConfig?.type === 'ONCE' && this.completedRuns === 1
  }

  public isOverdue(): boolean {
    return (
      !this.isCompleted() &&
      this.nextInspectionDue != null &&
      new Date() > new Date(this.nextInspectionDue)
    )
  }

  public static getDueDateColor(
    dueDate: string | undefined,
  ): string | undefined {
    if (!dueDate) return undefined
    const now = moment()
    const due = moment(dueDate)

    if (now.isAfter(due)) return 'error.main'
    return undefined
  }

  public isActive(): boolean {
    return this.scheduleConfig?.isActive === true
  }

  public toFormValues(): ScheduleInspectionFormValuesType | null {
    let frequency: ScheduleInspectionFrequencyType
    let customIntervalDays = 60
    if (!this.scheduleConfig) {
      return null
    }

    if (this.scheduleConfig.type === 'ONCE') {
      frequency = 'ONCE'
    } else {
      const days = this.scheduleConfig.frequencyInDays
      if (days === 1) frequency = 'DAILY'
      else if (days === 7) frequency = 'WEEKLY'
      else if (days === 30) frequency = 'MONTHLY'
      else {
        frequency = 'CUSTOM'
        customIntervalDays = days
      }
    }

    return {
      inspectionId: this.scheduleConfig.checklistTemplateId,
      frequency,
      dueDate: frequency === 'ONCE' ? this.nextInspectionDue : null,
      firstDueDate: frequency === 'ONCE' ? null : this.nextInspectionDue,
      customIntervalDays,
      excludeWeekends: this.scheduleConfig.excludeWeekends,
      assigneeUserId: this.assignedTo.id,
    }
  }

  public static getScheduleInspectionUpdatePayload(
    values: ScheduleInspectionFormValuesType,
  ): ScheduledInspectionUpdatePayloadType {
    const { frequency, inspectionId, excludeWeekends, customIntervalDays } =
      values

    const checklistTemplateId = inspectionId === undefined ? null : inspectionId

    let frequencyInDays: number | null
    let excludeWeekendsForConfig: boolean

    switch (frequency) {
      case 'ONCE':
        frequencyInDays = null
        excludeWeekendsForConfig = false
        break
      case 'DAILY':
        frequencyInDays = 1
        excludeWeekendsForConfig = excludeWeekends
        break
      case 'WEEKLY':
        frequencyInDays = 7
        excludeWeekendsForConfig = false
        break
      case 'MONTHLY':
        frequencyInDays = 30
        excludeWeekendsForConfig = excludeWeekends
        break
      case 'CUSTOM':
        frequencyInDays = customIntervalDays
        excludeWeekendsForConfig = excludeWeekends
        break
      default:
        frequencyInDays = 1
        excludeWeekendsForConfig = excludeWeekends
    }

    return {
      nextInspectionDue:
        values.frequency === 'ONCE' ? values.dueDate : values.firstDueDate,
      scheduleConfig: {
        type: frequency === 'ONCE' ? 'ONCE' : 'RECURRING',
        frequencyInDays,
        excludeWeekends: excludeWeekendsForConfig,
        isActive: true,
        checklistTemplateId,
      },
      assignedTo: values.assigneeUserId,
    }
  }

  public static new(payload: unknown): ScheduledInspection {
    return plainToInstance(ScheduledInspection, payload)
  }
}
